import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membersApi } from '../services/api';
import { Member, MemberRole } from '../types';
import { useAuth } from '../../context/AuthContext';

const ROLE_OPTIONS: { value: MemberRole; label: string; description: string }[] = [
  {
    value: 'student',
    label: 'Student',
    description: 'View moves, upload demonstrations, request feedback.',
  },
  {
    value: 'instructor',
    label: 'Instructor',
    description: 'Create moves and patterns, give feedback.',
  },
  {
    value: 'owner',
    label: 'Owner',
    description: 'Full admin access.',
  },
];

const ROLE_PRIORITY: Record<MemberRole, number> = {
  owner: 3,
  instructor: 2,
  student: 1,
};

const MembersManagement: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Member | null>(null);
  const [draftRoles, setDraftRoles] = useState<Set<MemberRole>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: members, isLoading, error } = useQuery({
    queryKey: ['members'],
    queryFn: membersApi.getAll,
  });

  const setRolesMutation = useMutation({
    mutationFn: ({ userId, roles }: { userId: number; roles: MemberRole[] }) =>
      membersApi.setRoles(userId, roles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setEditing(null);
    },
    onError: (err) => {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update roles';
      setActionError(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: membersApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: (err) => {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to remove member';
      setActionError(msg);
    },
  });

  useEffect(() => {
    if (editing) {
      setDraftRoles(new Set(editing.roles));
      setActionError(null);
    }
  }, [editing]);

  const filtered = (members ?? []).filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.email.toLowerCase().includes(q) || m.name.toLowerCase().includes(q);
  });

  const toggleDraft = (role: MemberRole) => {
    setDraftRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const saveRoles = () => {
    if (!editing) return;
    if (draftRoles.size === 0) {
      setActionError('Member must have at least one role. Use "Remove" to demote out of the tenant.');
      return;
    }
    setActionError(null);
    setRolesMutation.mutate({
      userId: editing.userId,
      roles: Array.from(draftRoles),
    });
  };

  const handleRemove = (m: Member) => {
    if (!window.confirm(`Remove ${m.email} from this tenant? Their account stays; they lose all roles here.`)) {
      return;
    }
    setActionError(null);
    removeMutation.mutate(m.userId);
  };

  const renderRoleChips = (roles: MemberRole[]) => (
    <div className="member-role-chips">
      {roles
        .slice()
        .sort((a, b) => ROLE_PRIORITY[b] - ROLE_PRIORITY[a])
        .map((r) => (
          <span
            key={r}
            className="member-role-chip"
            style={{
              background:
                r === 'owner'
                  ? '#fff3cd'
                  : r === 'instructor'
                    ? '#cfe2ff'
                    : '#e2e3e5',
            }}
          >
            {r}
          </span>
        ))}
    </div>
  );

  return (
    <div className="members-management">
      <div className="page-header">
        <h1>Members</h1>
      </div>

      <p className="admin-page-intro">
        Users with at least one Membership in this tenant. Edit a member's roles or
        remove them from the tenant entirely (their account stays — only the
        Memberships in this tenant are deleted). To add new people, use{' '}
        <strong>Invites</strong>.
      </p>

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search by email or name…"
          aria-label="Search members by email or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {actionError && (
        <div className="error-text member-error-banner">
          {actionError}
        </div>
      )}

      {error ? (
        <div className="error-text">Failed to load members.</div>
      ) : isLoading ? (
        <div className="loading">Loading members…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>{search ? 'No members match your search.' : 'No members yet.'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Roles</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const isSelf = !!user?.email && user.email.toLowerCase() === m.email.toLowerCase();
                return (
                  <tr key={m.userId}>
                    <td>
                      {m.email}
                      {isSelf && (
                        <span className="member-self-note">
                          (you)
                        </span>
                      )}
                    </td>
                    <td>{m.name}</td>
                    <td>{renderRoleChips(m.roles)}</td>
                    <td>{new Date(m.joinedAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => setEditing(m)}
                        >
                          Edit roles
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleRemove(m)}
                          disabled={removeMutation.isPending || isSelf}
                          title={isSelf ? 'You cannot remove yourself' : ''}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="modal-overlay member-modal-overlay"
          onClick={() => setEditing(null)}
        >
          <div
            className="form-container member-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Edit roles for {editing.email}</h2>
            <p className="admin-page-intro">
              Pick one or more roles. A user with multiple roles gets the highest-priority
              one as their primary role (owner &gt; instructor &gt; student).
            </p>

            <div className="role-option-list">
              {ROLE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="role-option-card"
                  style={{
                    background: draftRoles.has(opt.value) ? '#f0f7ff' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draftRoles.has(opt.value)}
                    onChange={() => toggleDraft(opt.value)}
                    className="role-option-input"
                  />
                  <div>
                    <div className="role-option-title">{opt.label}</div>
                    <div className="role-option-desc">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>

            {actionError && (
              <div className="error-text member-modal-error">
                {actionError}
              </div>
            )}

            <div className="form-actions member-modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveRoles}
                disabled={setRolesMutation.isPending}
              >
                {setRolesMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersManagement;
